import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, Image, Download, Settings, X } from 'lucide-react'
import toast from 'react-hot-toast'
import Dropdown from '../components/ui/Dropdown'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'
import { useSEO, createToolPageStructuredData, createBreadcrumbStructuredData, generateKeywords } from '../hooks/useSEO'

interface ConversionOptions {
  format: 'jpg' | 'png' | 'webp' | 'gif'
  quality: number
  preserveMetadata: boolean
}

interface ProcessedFile {
  id: string
  originalFile: File
  convertedBlob?: Blob
  originalFormat: string
  targetFormat: string
  originalSize: number
  convertedSize?: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
}

// Image conversion utility function
const convertImage = async (file: File, options: ConversionOptions): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = document.createElement('img')
    
    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }
      
      // Set white background for JPG format
      if (options.format === 'jpg') {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
      }
      
      ctx.drawImage(img, 0, 0)
      
      // Convert to target format
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Conversion failed'))
        }
      }, `image/${options.format}`, options.format === 'jpg' ? options.quality / 100 : undefined)
    }
    
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(file)
  })
}

const ImageConverter: React.FC = () => {
  // SEO for Image converter page
  useSEO({
    title: 'Image Converter - Convert JPG PNG WebP GIF Online Free | SnapTools',
    description: 'Convert image formats online free. Support JPG, PNG, WebP, GIF conversion with quality control. Professional image converter with batch processing. Fast, secure, no registration required.',
    keywords: generateKeywords('image', [
      'image converter online free',
      'convert image format',
      'JPG to PNG converter',
      'PNG to JPG converter',
      'WebP converter online',
      'GIF converter tool',
      'image format converter',
      'photo converter online',
      'picture converter free',
      'batch image converter',
      'convert images online',
      'free image converter',
      'online image converter',
      'web image converter',
      'browser image converter',
      'JPG converter online',
      'PNG converter online',
      'WebP image converter',
      'GIF image converter',
      'convert JPG to PNG',
      'convert PNG to JPG',
      'convert to WebP',
      'convert GIF online',
      'image format changer',
      'photo format converter',
      'picture format changer',
      'convert image type',
      'change image format',
      'image type converter'
    ]),
    canonical: 'https://snap-tools.online/image/convert',
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        createToolPageStructuredData(
          'Image Converter',
          'Convert image formats including JPG, PNG, WebP, and GIF online. Free, fast, and secure image conversion tool.',
          'https://snap-tools.online/image/convert'
        ),
        createBreadcrumbStructuredData([
          { name: 'Home', url: 'https://snap-tools.online/' },
          { name: 'Image Tools', url: 'https://snap-tools.online/' },
          { name: 'Image Converter', url: 'https://snap-tools.online/image/convert' }
        ])
      ]
    }
  })

  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<ConversionOptions>({
    format: 'jpg',
    quality: 85,
    preserveMetadata: false
  })

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const imageFiles = acceptedFiles.filter(file => 
      file.type.startsWith('image/')
    )

    if (imageFiles.length === 0) {
      toast.error('Please select valid image files')
      return
    }

    const newFiles: ProcessedFile[] = imageFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      originalFile: file,
      originalFormat: file.type.split('/')[1],
      targetFormat: options.format,
      originalSize: file.size,
      status: 'pending',
      progress: 0
    }))

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${imageFiles.length} image(s) for conversion`)
  }, [options.format])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif']
    },
    multiple: true
  })

  const processFiles = async () => {
    const pendingFiles = files.filter(file => file.status === 'pending')
    
    for (const file of pendingFiles) {
      try {
        // Update status to processing
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 10 } : f
        ))

        // Simulate processing progress
        for (let progress = 20; progress <= 90; progress += 20) {
          await new Promise(resolve => setTimeout(resolve, 200))
          setFiles(prev => prev.map(f => 
            f.id === file.id ? { ...f, progress } : f
          ))
        }

        // Perform actual image conversion
        const convertedBlob = await convertImage(file.originalFile, options)
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { 
            ...f, 
            convertedBlob,
            convertedSize: convertedBlob.size,
            status: 'completed',
            progress: 100
          } : f
        ))

        toast.success(`Converted ${file.originalFile.name}`)
      } catch (error) {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to convert ${file.originalFile.name}`)
      }
    }
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.convertedBlob) return

    const url = URL.createObjectURL(file.convertedBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.originalFile.name.split('.')[0]}.${file.targetFormat}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <Image className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            Image Format Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Convert your images between JPG, PNG, WebP, and GIF formats with professional quality and lightning speed.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-8"
        />

        {/* Options Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8 mb-8"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-[#8438ff]/10 rounded-xl flex items-center justify-center">
              <Settings className="h-5 w-5 text-[#8438ff]" />
            </div>
            <h2 className="text-xl font-bold text-[#8438ff]">Conversion Settings</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <label className="block text-sm font-bold text-[#8438ff] mb-3">
                Output Format
              </label>
              <Dropdown
                options={[
                  {
                    value: 'jpg',
                    label: 'JPG',
                    description: 'Smaller files, no transparency'
                  },
                  {
                    value: 'png',
                    label: 'PNG',
                    description: 'Larger files, supports transparency'
                  },
                  {
                    value: 'webp',
                    label: 'WebP',
                    description: 'Modern format, excellent compression'
                  },
                  {
                    value: 'gif',
                    label: 'GIF',
                    description: 'Limited colors, supports animation'
                  }
                ]}
                value={options.format}
                onChange={(value) => setOptions(prev => ({ ...prev, format: value as 'jpg' | 'png' | 'webp' | 'gif' }))}
                placeholder="Select output format"
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-[#8438ff] mb-3">
                Quality: {options.quality}%
              </label>
              <div className="relative">
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={options.quality}
                  onChange={(e) => setOptions(prev => ({ ...prev, quality: parseInt(e.target.value) }))}
                  className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  disabled={options.format === 'png'}
                  style={{
                    background: `linear-gradient(to right, #8438ff 0%, #8438ff ${options.quality}%, #e5e7eb ${options.quality}%, #e5e7eb 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Low</span>
                  <span>High</span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col justify-center">
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
                <input
                  type="checkbox"
                  id="preserveMetadata"
                  checked={options.preserveMetadata}
                  onChange={(e) => setOptions(prev => ({ ...prev, preserveMetadata: e.target.checked }))}
                  className="w-5 h-5 rounded"
                />
                <label htmlFor="preserveMetadata" className="text-sm font-bold text-[#8438ff]">
                  Preserve Metadata
                </label>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upload Zone */}
        <div
          className={`upload-zone ${isDragActive ? 'active' : ''} mb-8 bg-gradient-to-br from-gray-50 to-white border-2 border-dashed transition-all duration-300`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center">
            <div className="w-20 h-20 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-full flex items-center justify-center mb-6 shadow-lg">
              <Upload className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#8438ff] mb-2">
              {isDragActive ? 'Drop your images here' : 'Drag & drop images here'}
            </h3>
            <p className="text-gray-600 mb-4">
              or <span className="text-[#8438ff] font-bold cursor-pointer hover:underline">browse files</span>
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span className="bg-gray-100 px-3 py-1 rounded-full">JPG</span>
              <span className="bg-gray-100 px-3 py-1 rounded-full">PNG</span>
              <span className="bg-gray-100 px-3 py-1 rounded-full">WebP</span>
              <span className="bg-gray-100 px-3 py-1 rounded-full">GIF</span>
            </div>
            <p className="text-xs text-gray-400 mt-2">Max 100MB per file</p>
          </div>
        </div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.content}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#8438ff]">
                Files ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={files.filter(f => f.status === 'pending').length === 0}
                className="bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                Convert All
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-xl p-6 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#8438ff]/10 rounded-lg flex items-center justify-center">
                        <Image className="h-5 w-5 text-[#8438ff]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#8438ff]">{file.originalFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {file.originalFormat.toUpperCase()} → {file.targetFormat.toUpperCase()} • {formatFileSize(file.originalSize)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && file.convertedBlob && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="p-2 text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {(file.status === 'processing' || file.status === 'completed') && (
                    <div className="progress-bar mb-2">
                      <div 
                        className="progress-fill"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  )}

                  {/* Status */}
                  <div className="flex items-center justify-between text-sm">
                    <span className={`font-medium
                      ${file.status === 'pending' ? 'text-gray-500' : ''}
                      ${file.status === 'processing' ? 'text-[#8438ff]' : ''}
                      ${file.status === 'completed' ? 'text-green-600' : ''}
                      ${file.status === 'error' ? 'text-red-600' : ''}
                    `}>
                      {file.status === 'pending' && 'Ready to convert'}
                      {file.status === 'processing' && 'Converting...'}
                      {file.status === 'completed' && 'Conversion complete'}
                      {file.status === 'error' && 'Conversion failed'}
                    </span>
                    
                    {file.status === 'completed' && file.convertedSize && (
                      <span className="text-gray-500 font-medium">
                        {formatFileSize(file.convertedSize)} 
                        {file.convertedSize < file.originalSize && (
                          <span className="text-green-600 ml-1">
                            (-{Math.round((1 - file.convertedSize / file.originalSize) * 100)}%)
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default ImageConverter
